/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    MYBROADCASTS_PHASE2_REFRESH_SUPPORT
 * Date:         2025-01-02
 * Modified By:  Cursor
 *
 * Description:
 * Export refreshMine function from useBroadcastsList hook to allow
 * manual re-fetching of "My Broadcasts" data after actions.
 *
 * File Size Check:
 * - Current: 181 lines
 * - Adding: ~15 lines
 * - Projected: 196 lines
 * - Decision: Proceed (within limit)
 *
 * Related Screens / Modules:
 * - client/src/features/broadcast/pages/broadcasts-list/hooks/useBroadcastsList.ts
 * - client/src/features/broadcast/pages/broadcasts-list/components/BroadcastsTabs.tsx
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    ALLBROADCASTS_SIDEBAR_FILTER
 * Date:         2026-01-01
 * Modified By:  Cursor
 *
 * Description:
 * Route changed from /broadcasts to /allbroadcasts.
 *
 * File Size Check:
 * - Current: 162 lines
 * - Adding: ~5 lines
 * - Projected: 167 lines
 * - Decision: Proceed (within limit)
 *
 * Related Screens / Modules:
 * - client/src/features/broadcast/pages/broadcasts-list/hooks/useBroadcastsList.ts
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BROADCASTS_FILTER_INTEGRATION
 * Date:         2026-01-01
 * Modified By:  Cursor
 *
 * Description:
 * Integrated useBroadcastFilters hook for filter state management.
 *
 * File Size Check:
 * - Current: 155 lines
 * - Adding: ~15 lines
 * - Projected: 170 lines
 * - Decision: Proceed (within limit)
 *
 * Related Screens / Modules:
 * - client/src/features/broadcast/pages/broadcasts-list/hooks/useBroadcastsList.ts
 * - client/src/features/broadcast/pages/broadcasts-list/hooks/useBroadcastFilters.ts
 */

import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { listBroadcastsRaw, listAllBroadcastsWithUsers } from "../../../../../services/broadcasts";
import { Broadcast, BroadcastListResponse } from "../../../../../interfaces/broadcast";
import { getErrorMessage } from "../../../../../utils/notifications";
import { useBroadcastFilters } from "./useBroadcastFilters";

export type TabKey = "recent" | "wts" | "wtb" | "mine";

// [2025-12-31] Preview limit for broadcasts page (will add pagination in Phase 5)
const BROADCASTS_PAGE_LIMIT = 50;

export const useBroadcastsList = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // [2025-12-31] Read initial tab from URL parameter
  const getTabFromUrl = useCallback((): TabKey => {
    const searchParams = new URLSearchParams(location.search);
    const urlTab = searchParams.get("tab");
    const validTabs: TabKey[] = ["recent", "wts", "wtb", "mine"];
    return (urlTab && validTabs.includes(urlTab as TabKey)) ? urlTab as TabKey : "recent";
  }, [location.search]); // [2025-01-01] ESLint fix: Wrapped in useCallback

  const [activeTab, setActiveTab] = useState<TabKey>(getTabFromUrl());

  // [2025-12-31] State for Recent tab
  const [recentData, setRecentData] = useState<Broadcast[]>([]);
  const [recentLoading, setRecentLoading] = useState(true);
  const [recentError, setRecentError] = useState<string | null>(null);

  // [2025-12-31] State for WTS tab
  const [wtsData, setWtsData] = useState<Broadcast[]>([]);
  const [wtsLoading, setWtsLoading] = useState(true);
  const [wtsError, setWtsError] = useState<string | null>(null);

  // [2025-12-31] State for WTB tab
  const [wtbData, setWtbData] = useState<Broadcast[]>([]);
  const [wtbLoading, setWtbLoading] = useState(true);
  const [wtbError, setWtbError] = useState<string | null>(null);

  // [2025-12-31] State for My Broadcasts tab
  const [mineData, setMineData] = useState<Broadcast[]>([]);
  const [mineLoading, setMineLoading] = useState(true);
  const [mineError, setMineError] = useState<string | null>(null);

  // [2025-12-31] Update active tab when URL changes
  useEffect(() => {
    const newTab = getTabFromUrl();
    setActiveTab(newTab);
  }, [location.search, getTabFromUrl]); // [2025-01-01] ESLint fix: Added missing dependency

  // [2025-12-31] Generic fetcher for tabs
  const fetchTabData = async (
    fetchFn: (params: any) => Promise<BroadcastListResponse>,
    params: any,
    setData: (data: Broadcast[]) => void,
    setLoading: (loading: boolean) => void,
    setError: (error: string | null) => void,
    mounted: boolean,
    errorMsg: string,
    limit: number | null = BROADCASTS_PAGE_LIMIT
  ) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchFn({ page: 1, limit: limit, ...params });
      if (mounted) setData(res.items || []);
    } catch (e: any) {
      if (mounted) setError(getErrorMessage(e, errorMsg));
    } finally {
      if (mounted) setLoading(false);
    }
  };

  // [2025-12-31] Fetch all tab data
  useEffect(() => {
    let mounted = true;
    
    // Recent
    fetchTabData(listAllBroadcastsWithUsers, {}, setRecentData, setRecentLoading, setRecentError, mounted, "Failed to load broadcasts");
    
    // WTS
    fetchTabData(listAllBroadcastsWithUsers, { type: "WTS" }, setWtsData, setWtsLoading, setWtsError, mounted, "Failed to load WTS broadcasts");
    
    // WTB
    fetchTabData(listAllBroadcastsWithUsers, { type: "WTB" }, setWtbData, setWtbLoading, setWtbError, mounted, "Failed to load WTB broadcasts");
    
    // Mine
    fetchTabData(listBroadcastsRaw, {}, setMineData, setMineLoading, setMineError, mounted, "Failed to load broadcasts", null);
    
    return () => { mounted = false; };
  }, []);

  // [2025-01-02] Phase 2: Manual refresh for "My Broadcasts" tab
  const refreshMine = useCallback(async () => {
    await fetchTabData(
      listBroadcastsRaw, 
      {}, 
      setMineData, 
      setMineLoading, 
      setMineError, 
      true, 
      "Failed to load broadcasts",
      null // Fetch all for "mine" tab
    );
  }, []);

// [2026-02-13] UX: Default filters to collapsed for cleaner initial view
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);

  const toggleFilters = () => {
    setFiltersCollapsed(!filtersCollapsed);
  };

  // [2025-12-31] Phase 5: Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // [2026-01-01] Broadcast filter state from dedicated hook
  const filterState = useBroadcastFilters();

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newLimit: number) => {
    setItemsPerPage(newLimit);
    setCurrentPage(1); // Reset to first page
  };

  // [2026-01-01] Handle tab change - update URL parameter (route changed to /allbroadcasts)
  const handleTabChange = (newTab: TabKey) => {
    navigate(`/allbroadcasts?tab=${newTab}`);
  };

  return {
    activeTab,
    handleTabChange,
    recent: { data: recentData, loading: recentLoading, error: recentError },
    wts: { data: wtsData, loading: wtsLoading, error: wtsError },
    wtb: { data: wtbData, loading: wtbLoading, error: wtbError },
    mine: { data: mineData, loading: mineLoading, error: mineError },
    refreshMine, // [2025-01-02] Phase 2: Export refresh function
    // [2025-12-31] Phase 4: Filters
    filtersCollapsed,
    toggleFilters,
    // [2025-12-31] Phase 5: Pagination
    currentPage,
    itemsPerPage,
    handlePageChange,
    handleItemsPerPageChange,
    // [2026-01-01] Broadcast filters
    ...filterState,
  };
};
